import {useCallback, useState} from 'react';

import {Cell} from 'notebook/tables/constants/CellConstants';
import {Row} from 'notebook/tables/TableConstants';

import {
  CrossModelComparisonTables,
  getComparisonCellDisplayText,
  MismatchErrorType,
} from './CrossModelValidation';

export interface CellEdit {
  rowIndex: number;
  columnIndex: number;
  newValue: string;
}

export interface ModelEdits {
  [tableName: string]: {
    [modelIndex: number]: {
      [key: string]: CellEdit;
    };
  };
}

export interface FlexibleDataEntryResultEditsControls {
  modelEdits: ModelEdits;
  handleCellEdit: (
    modelIndex: number,
    rowIndex: number,
    columnIndex: number,
    newValue: string | null,
    tableName: string
  ) => void;
  clearModelEdits: () => void;
}

function getKey(rowIndex: number, columnIndex: number): string {
  return `${rowIndex}:${columnIndex}`;
}
/**
 * Hook to manage edits made to model results in the flexible data entry UI.
 */
export function useFlexibleDataEntryResultEditsControls(
  tableNames: Array<string>
): FlexibleDataEntryResultEditsControls {
  const [modelEdits, setModelEdits] = useState<ModelEdits>(
    Object.fromEntries(tableNames.map((tableName) => [tableName, {}]))
  );

  /**
   * Handles editing a cell in a specific model result. Null values are used to remove edits.
   */
  const handleCellEdit = useCallback(
    (
      modelIndex: number,
      rowIndex: number,
      columnIndex: number,
      newValue: string | null,
      tableName: string
    ) => {
      const indexingKey = getKey(rowIndex, columnIndex);
      setModelEdits((prevModelEdits) => {
        const newModelEdits = {...prevModelEdits};
        newModelEdits[tableName][modelIndex] = newModelEdits[tableName][modelIndex] ?? {};

        if (newValue == null) {
          delete newModelEdits[tableName][modelIndex][indexingKey];
        } else {
          newModelEdits[tableName][modelIndex][indexingKey] = {
            rowIndex,
            columnIndex,
            newValue,
          };
        }

        return newModelEdits;
      });
    },
    []
  );

  /**
   * Clears all edits.
   */
  const clearModelEdits = useCallback(() => {
    setModelEdits(Object.fromEntries(tableNames.map((tableName) => [tableName, {}])));
  }, [tableNames]);

  return {modelEdits, handleCellEdit, clearModelEdits};
}

/**
 * Applies model edits to the cross model comparison tables.
 * Updates cell text and adds appropriate error messages for edited cells.
 */
export function applyModelEdits(
  tables: CrossModelComparisonTables,
  modelEdits: ModelEdits,
  modelIndex: number,
  tableName: string
): CrossModelComparisonTables {
  const editsForModel = modelEdits[tableName]?.[modelIndex] ?? {};

  return {
    ...tables,
    primaryTable: tables.primaryTable.map((row, rowIndex) =>
      row.map((cell, columnIndex) => {
        const edit = editsForModel[getKey(rowIndex, columnIndex)];
        if (edit != null) {
          return {
            ...cell,
            userText: edit.newValue,
            error: {
              type: MismatchErrorType.USER_EDIT,
              message: `Manually edited (original value: "${getComparisonCellDisplayText(cell)}")`,
            },
          };
        }
        return cell;
      })
    ),
  };
}

export function updateResultsWithEdits(
  rows: Array<Row>,
  modelEdits: ModelEdits,
  modelIndex: number,
  tableName: string
): Array<Row> {
  const editsForModel = modelEdits[tableName]?.[modelIndex] ?? {};

  return rows.map((row, rowIndex) =>
    row.map((cell: Cell, columnIndex: number) => {
      const key = getKey(rowIndex, columnIndex);
      const edit = editsForModel[key];

      if (edit == null) {
        return cell;
      }

      return {
        ...cell,
        text: edit.newValue,
      };
    })
  );
}
